/**
 * O relógio da fila de sincronização — card 1864753210497959381.
 *
 * O estado que o motor do Affine publica (`engine.doc.state$`) responde *o que* está
 * pendente e **nunca** responde *há quanto tempo*. É por isso que uma fila que drena em
 * 200 ms e uma fila parada há 36 minutos produzem a mesma tela: o único consumidor visual
 * (`workspace-card/index.tsx`) lê `syncing > 0` e desenha `Syncing...`, sem ramo para
 * "isto já devia ter acabado".
 *
 * Este arquivo é a dimensão que faltava, e ele é PURO de propósito: recebe uma amostra e o
 * estado anterior, devolve o novo. Sem timer, sem rxjs, sem DOM — é o que permite testar os
 * dois limiares sem navegador.
 */

/** Já sincronizou nesta sessão e parou: 60 s é muito tempo para uma fila de editor. */
export const LIMIAR_APOS_SINCRONIZAR_MS = 60_000;

/**
 * Nunca sincronizou nesta sessão: a carga inicial é legitimamente lenta.
 *
 * ⚠️ Este segundo limiar não é frouxidão — é o que impede o alarme que se aprende a
 * ignorar. Enquanto o peer não conectou, `peerState$` reporta `syncing = docs.size`, ou
 * seja, **TODOS** os documentos do workspace. Com um limiar só, toda abertura de workspace
 * grande nasceria vermelha.
 */
export const LIMIAR_DE_CARGA_INICIAL_MS = 180_000;

export type Gravidade =
  /** o workspace não fala com servidor nenhum — não há o que prometer */
  | 'desligado'
  /** o servidor tem tudo */
  | 'em-dia'
  /** há fila, e ainda está dentro do tempo esperado */
  | 'subindo'
  /** há fila há tempo demais */
  | 'travado'
  /** o motor está retentando, ou reportou erro */
  | 'erro';

export interface AmostraDoMotor {
  /** `state$.syncing` — quantos documentos têm trabalho pendente */
  pendentes: number;
  /** `state$.synced` */
  sincronizado: boolean;
  /** `state$.retrying` */
  retentando: boolean;
  /** `state$.errorMessage` */
  erro: string | null;
  /** o workspace tem servidor? (`flavour !== 'local'`) */
  temServidor: boolean;
  /** `navigator.onLine` */
  online: boolean;
}

export interface EstadoDoRelogio {
  gravidade: Gravidade;
  /** ms desde que a fila deixou de estar vazia — `null` quando em dia */
  pendenteHa: number | null;
  /** quando a fila deixou de estar vazia (epoch ms) — `null` quando em dia */
  pendenteDesde: number | null;
  /** último `synced === true` desta sessão (epoch ms) — `null` se nunca houve */
  sincronizadoEm: number | null;
  /** falso durante toda a carga inicial; é ele que escolhe o limiar */
  jaSincronizouNestaSessao: boolean;
  erro: string | null;
  online: boolean;
  /** o limiar que valeu nesta avaliação, para o texto poder explicá-lo */
  limiarMs: number;
}

export const ESTADO_INICIAL: EstadoDoRelogio = {
  gravidade: 'desligado',
  pendenteHa: null,
  pendenteDesde: null,
  sincronizadoEm: null,
  jaSincronizouNestaSessao: false,
  erro: null,
  online: true,
  limiarMs: LIMIAR_DE_CARGA_INICIAL_MS,
};

/**
 * Avalia uma amostra do motor contra o estado anterior.
 *
 * ⚠️ `agora` entra por parâmetro, nunca por `Date.now()` lá dentro: um relógio que o teste
 * não controla é um relógio que o teste não mede.
 */
export function avaliar(
  amostra: AmostraDoMotor,
  anterior: EstadoDoRelogio,
  agora: number
): EstadoDoRelogio {
  if (!amostra.temServidor) {
    // Workspace local. Não há promessa de servidor para quebrar, então não há aviso a dar —
    // e um aviso aqui seria mentira sobre um dado que nunca ia subir.
    return { ...ESTADO_INICIAL, online: amostra.online };
  }

  if (amostra.sincronizado) {
    return {
      gravidade: 'em-dia',
      pendenteHa: null,
      pendenteDesde: null,
      sincronizadoEm: agora,
      jaSincronizouNestaSessao: true,
      erro: null,
      online: amostra.online,
      limiarMs: LIMIAR_APOS_SINCRONIZAR_MS,
    };
  }

  const pendenteDesde = anterior.pendenteDesde ?? agora;
  const pendenteHa = agora - pendenteDesde;
  const limiarMs = anterior.jaSincronizouNestaSessao
    ? LIMIAR_APOS_SINCRONIZAR_MS
    : LIMIAR_DE_CARGA_INICIAL_MS;

  // Erro e retentativa ganham do relógio: o motor já SABE que algo deu errado, e esperar
  // 60 s para dizer o que ele já disse seria guardar a informação de quem precisa dela.
  const temErro = amostra.retentando || !!amostra.erro;

  const gravidade: Gravidade = temErro
    ? 'erro'
    : pendenteHa >= limiarMs
      ? 'travado'
      : 'subindo';

  return {
    gravidade,
    pendenteHa,
    pendenteDesde,
    sincronizadoEm: anterior.sincronizadoEm,
    jaSincronizouNestaSessao: anterior.jaSincronizouNestaSessao,
    erro: amostra.erro,
    online: amostra.online,
    limiarMs,
  };
}

/** O aviso aparece a partir daqui. `subindo` é a operação normal e não merece faixa. */
export function mereceAviso(estado: EstadoDoRelogio): boolean {
  return estado.gravidade === 'travado' || estado.gravidade === 'erro';
}

/** Há trabalho que ainda não saiu deste navegador? É o que o `beforeunload` pergunta. */
export function temFilaPendente(estado: EstadoDoRelogio): boolean {
  return (
    estado.gravidade === 'subindo' ||
    estado.gravidade === 'travado' ||
    estado.gravidade === 'erro'
  );
}
